import * as React from 'react'
// antd's OWN contexts, not transcribed copies. `DisabledContext` is what a
// `<ConfigProvider componentDisabled>` cascade writes (the held-lease read-only
// mode uses it), and `FormItemInputContext` is how an enclosing `Form.Item`
// publishes its validation status. Both are still real on this branch, so
// reading them here keeps a shadcn-mode Input obeying exactly what an
// antd-mode one obeys.
import DisabledContext from 'antd/es/config-provider/DisabledContext'
import { FormItemInputContext } from 'antd/es/form/context'
import type { InputProps, InputRef } from 'antd/es/input'
import type { PasswordProps } from 'antd/es/input/Password'
import type { SearchProps } from 'antd/es/input/Search'
import type { TextAreaProps, TextAreaRef } from 'antd/es/input/TextArea'

import { Input as InputPrimitive } from 'src/components/ui/input'
import { Textarea as TextareaPrimitive } from 'src/components/ui/textarea'
import {
  CloseCircleFilled,
  EyeInvisibleOutlined,
  EyeOutlined,
  LoadingOutlined,
  SearchOutlined,
} from 'src/icons/antd-compat'
import { cn } from 'src/lib/utils'

/* -------------------------------------------------------------------------
 * The SHADCN half of the `src/antd/Input` seam. `./Input.tsx` is the
 * dispatcher that chooses between this and antd's own Input; nothing else
 * imports this file, and nothing reaches it until someone flips the design
 * toggle (`src/design-mode`).
 *
 * Ported from the `update-design` reference branch's `src/antd/Input.tsx`
 * (its Phase 2 slice 5). Divergences from the reference are marked
 * `[development]` below; everything else is the reference verbatim.
 *
 * The EXTERNAL API is antd's own `InputProps` / `TextAreaProps` /
 * `PasswordProps` / `SearchProps` and antd's `InputRef` / `TextAreaRef`, so
 * the 290 files that already import `Input` from `src/antd` switch with no
 * edit at all — including `src/ui/DebouncedSearch.tsx`, which calls
 * `ref.current.focus()`, and every `Form.Item` child, which rc-field-form
 * clones with `value` / `onChange` / `id` and whose ref it uses to scroll to
 * the first invalid field.
 *
 * [development] The reference TRANSCRIBES antd's prop types and reads
 * transcribed copies of the two contexts, because antd is deleted over there.
 * Here both are real, so this file imports antd's — the types keep every call
 * site's inference identical, and the contexts keep the two cascades live.
 *
 * [development] `.ant-input*` IS NOT RE-EMITTED. antd is installed and the
 * toggle is a RUNTIME flip: any page that rendered an antd Input before the
 * flip has antd's input stylesheet on it, UNLAYERED — and unlike Card or
 * Alert, Input's rules are on the page even in a session that never flipped
 * back, because antd renders its OWN Input inside AutoComplete and inside a
 * Table's filter search. `.ant-input` carries height, padding, border,
 * background and font-size, i.e. exactly what this seam replaces, so it is the
 * `.ant-btn` situation. The parts are keyed by `data-slot` only, and the live
 * stylesheets that reached into antd's names each grew a `[data-slot='…']`
 * selector beside the `.ant-input-…` one (the same treatment `../Tag` gave
 * its three).
 *
 * NOT PORTED, deliberately, and measured:
 *   • `Input.Group` — antd's deprecated addon group. 0 call sites; its
 *     replacement, `addonBefore`/`addonAfter`, IS ported and is what the 27
 *     live addon call sites use.
 *   • The Tooltip-trigger marking the reference applies to `Input`,
 *     `TextArea` and `Password` (`markPointerInertWhenDisabled`). That helper
 *     belongs to the reference's own Tooltip seam, which is not on this branch
 *     — a disabled input as a tooltip trigger is exactly as dead here as it is
 *     with antd today, so nothing regresses; it is a gap to close with the
 *     Tooltip port, not with this one.
 * ------------------------------------------------------------------------- */

type InputStatus = '' | 'error' | 'warning' | undefined
type InputVariant = InputProps['variant']

interface InputFocusOptions extends FocusOptions {
  cursor?: 'start' | 'end' | 'all'
}

function triggerFocus(
  el: HTMLInputElement | HTMLTextAreaElement | null,
  options?: InputFocusOptions
) {
  if (!el) return
  el.focus(options)
  const { cursor } = options ?? {}
  if (!cursor) return
  const len = el.value.length
  if (cursor === 'start') el.setSelectionRange(0, 0)
  else if (cursor === 'end') el.setSelectionRange(len, len)
  else el.setSelectionRange(0, len)
}

/**
 * The `warning` status only. `error` rides on `aria-invalid`, which the shadcn
 * primitives already style — one source for the error look, so a field cannot
 * read as invalid to a screen reader and valid to the eye.
 */
const statusClassName = (status: InputStatus) =>
  status === 'warning' &&
  'border-wh-gold-500 focus-visible:border-wh-gold-500 focus-visible:ring-wh-gold-500/20'

const variantClassName = (variant: InputVariant) => {
  if (variant === 'borderless')
    return 'border-transparent bg-transparent shadow-none focus-visible:ring-0'
  if (variant === 'filled') return 'border-transparent bg-muted shadow-none'
  return undefined
}

// forwardRef: React 18 runtime (no ref-as-prop) — exposes antd's InputRef
// shape (.focus()/.blur()/.select()/.setSelectionRange()/.input) so callers
// like src/ui/DebouncedSearch.tsx keep working unchanged.
const InputBase = React.forwardRef<InputRef, InputProps>(
  (
    {
      className,
      style,
      value: valueProp,
      defaultValue,
      onChange,
      onPressEnter,
      onKeyDown,
      prefix,
      suffix,
      addonBefore,
      addonAfter,
      allowClear,
      size,
      status: statusProp,
      variant,
      bordered,
      disabled: disabledProp,
      readOnly,
      htmlSize,
      // antd-only props that must never reach the DOM
      prefixCls: _prefixCls,
      rootClassName: _rootClassName,
      classNames: _classNames,
      styles: _styles,
      showCount: _showCount,
      count: _count,
      ...rest
    },
    ref
  ) => {
    const contextDisabled = React.useContext(DisabledContext)
    const formItemContext = React.useContext(FormItemInputContext)
    const disabled = disabledProp ?? contextDisabled
    const status = (statusProp ?? formItemContext?.status) as InputStatus
    const inputRef = React.useRef<HTMLInputElement>(null)
    const [uncontrolledValue, setUncontrolledValue] = React.useState(
      defaultValue ?? ''
    )
    const value = valueProp ?? uncontrolledValue
    const resolvedVariant: InputVariant =
      variant ?? (bordered === false ? 'borderless' : 'outlined')

    React.useImperativeHandle(ref, () => ({
      focus: (options?: InputFocusOptions) =>
        triggerFocus(inputRef.current, options),
      blur: () => inputRef.current?.blur(),
      setSelectionRange: (
        start: number,
        end: number,
        direction?: 'forward' | 'backward' | 'none'
      ) => inputRef.current?.setSelectionRange(start, end, direction),
      select: () => inputRef.current?.select(),
      input: inputRef.current,
      nativeElement: inputRef.current,
    }))

    const handleChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
      if (valueProp === undefined) setUncontrolledValue(e.target.value)
      onChange?.(e)
    }

    const handleKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
      if (e.key === 'Enter') onPressEnter?.(e)
      onKeyDown?.(e)
    }

    const clear = () => {
      // Dispatching a native input event is what makes a controlled call site
      // — every `Form.Item` child — see the clear at all: React's onChange is
      // delegated off the native event, so setting `.value` alone is invisible.
      const nativeSetter = Object.getOwnPropertyDescriptor(
        window.HTMLInputElement.prototype,
        'value'
      )?.set
      if (inputRef.current && nativeSetter) {
        nativeSetter.call(inputRef.current, '')
        inputRef.current.dispatchEvent(new Event('input', { bubbles: true }))
      }
      if (valueProp === undefined) setUncontrolledValue('')
      inputRef.current?.focus()
    }

    const clearIcon =
      typeof allowClear === 'object' ? allowClear.clearIcon : undefined
    const showClear = allowClear && !disabled && String(value ?? '').length > 0

    // Structural: whether the affix wrapper renders at all must NOT depend on
    // `showClear` (value-length-dependent) — flipping the wrapper in/out as
    // the user types past the first character changes the input's position
    // in the tree, so React remounts it mid-typing and drops every keystroke
    // after the first. Whether allowClear is CONFIGURED is stable; whether
    // the clear button is currently VISIBLE is just conditional content
    // inside the (always-present-when-configured) wrapper.
    const hasAffix = Boolean(prefix || suffix || allowClear)

    const input = (
      <InputPrimitive
        aria-invalid={status === 'error' || undefined}
        {...(rest as React.ComponentProps<'input'>)}
        ref={inputRef}
        size={htmlSize}
        value={value as string | number | readonly string[]}
        disabled={disabled}
        readOnly={readOnly}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        className={cn(
          hasAffix
            ? 'h-auto flex-1 border-0 bg-transparent p-0 shadow-none focus-visible:ring-0'
            : cn(
                size === 'small' && 'h-7 text-sm',
                size === 'large' && 'h-11',
                statusClassName(status),
                variantClassName(resolvedVariant),
                !addonBefore && !addonAfter && className
              )
        )}
        style={!hasAffix && !addonBefore && !addonAfter ? style : undefined}
      />
    )

    const affixWrapped = hasAffix ? (
      <div
        data-slot="input-affix-wrapper"
        data-disabled={disabled || undefined}
        className={cn(
          /* `has-[input[aria-invalid]]:border-destructive` is what carries the
             ERROR state onto this wrapper. The shadcn primitive paints an
             invalid field by styling the `<input>` itself, but an affixed
             input is stripped to `border-0 bg-transparent` and the border
             belongs to this box — so without it, a field with a prefix, a
             suffix or a clear button reads as valid however loudly antd's
             `Form.Item` disagrees. Found while porting `./InputNumber`, whose
             steppers ride in the `suffix` slot and therefore make this the
             DEFAULT case rather than an edge one; browser-measured against a
             plain `<Input status="error">`, which was red while the affixed
             one was not. `aria-invalid` is set from `status` OR from
             `FormItemInputContext`, so one selector covers both. */
          'flex h-9 w-full items-center gap-1.5 rounded-md border border-input bg-transparent px-3 shadow-xs transition-[color,box-shadow] has-[input[aria-invalid]]:border-destructive has-[input:focus-visible]:border-ring has-[input:focus-visible]:ring-[3px] has-[input:focus-visible]:ring-ring/50',
          size === 'small' && 'h-7',
          size === 'large' && 'h-11',
          disabled && 'cursor-not-allowed opacity-50',
          statusClassName(status),
          variantClassName(resolvedVariant),
          !addonBefore && !addonAfter && className
        )}
        style={!addonBefore && !addonAfter ? style : undefined}
      >
        {prefix ? (
          <span
            data-slot="input-prefix"
            className="flex shrink-0 items-center text-muted-foreground"
          >
            {prefix}
          </span>
        ) : null}
        {input}
        {showClear ? (
          /* Deliberately FLUSH — no padding utility. The `[data-slot]` base
              layer resets Chrome's UA `button { padding: 1px 6px }`, and that
              is what this affix wants: it is an icon-only child of the
              affix wrapper, which owns the input's horizontal padding, and
              antd's own clear icon sits flush inside it too. */
          <button
            type="button"
            data-slot="input-clear-icon"
            aria-label="clear"
            className="flex shrink-0 cursor-pointer items-center text-muted-foreground/60 hover:text-muted-foreground"
            onClick={clear}
          >
            {clearIcon ?? <CloseCircleFilled />}
          </button>
        ) : null}
        {suffix ? (
          <span
            data-slot="input-suffix"
            className="flex shrink-0 items-center text-muted-foreground"
          >
            {suffix}
          </span>
        ) : null}
      </div>
    ) : (
      input
    )

    if (!addonBefore && !addonAfter) return affixWrapped

    return (
      <div
        data-slot="input-group"
        className={cn('flex w-full items-stretch', className)}
        style={style}
      >
        {addonBefore ? (
          <span
            data-slot="input-addon"
            className="inline-flex items-center rounded-l-md border border-r-0 border-input bg-muted px-3 text-sm text-muted-foreground"
          >
            {addonBefore}
          </span>
        ) : null}
        {React.cloneElement(affixWrapped, {
          className: cn(
            affixWrapped.props.className,
            'min-w-0 flex-1',
            addonBefore && 'rounded-l-none',
            addonAfter && 'rounded-r-none'
          ),
        })}
        {addonAfter ? (
          <span
            data-slot="input-addon"
            className="inline-flex items-center rounded-r-md border border-l-0 border-input bg-muted px-3 text-sm text-muted-foreground"
          >
            {addonAfter}
          </span>
        ) : null}
      </div>
    )
  }
)
InputBase.displayName = 'Input'

// forwardRef: exposes antd's TextAreaRef shape (.focus()/.blur()).
const TextArea = React.forwardRef<TextAreaRef, TextAreaProps>(
  (
    {
      className,
      style,
      value: valueProp,
      defaultValue,
      onChange,
      onPressEnter,
      onKeyDown,
      showCount,
      autoSize,
      status: statusProp,
      variant,
      bordered,
      size,
      disabled: disabledProp,
      maxLength,
      rows,
      // antd-only props that must never reach the DOM
      prefixCls: _prefixCls,
      rootClassName: _rootClassName,
      classNames: _classNames,
      styles: _styles,
      allowClear: _allowClear,
      count: _count,
      ...rest
    },
    ref
  ) => {
    const contextDisabled = React.useContext(DisabledContext)
    const formItemContext = React.useContext(FormItemInputContext)
    const disabled = disabledProp ?? contextDisabled
    const status = (statusProp ?? formItemContext?.status) as InputStatus
    const textareaRef = React.useRef<HTMLTextAreaElement>(null)
    const [uncontrolledValue, setUncontrolledValue] = React.useState(
      defaultValue ?? ''
    )
    const value = valueProp ?? uncontrolledValue

    React.useImperativeHandle(
      ref,
      () =>
        ({
          focus: (options?: InputFocusOptions) =>
            triggerFocus(textareaRef.current, options),
          blur: () => textareaRef.current?.blur(),
          resizableTextArea: textareaRef.current
            ? { textArea: textareaRef.current }
            : undefined,
          nativeElement: textareaRef.current,
        } as unknown as TextAreaRef)
    )

    const handleChange: React.ChangeEventHandler<HTMLTextAreaElement> = (e) => {
      if (valueProp === undefined) setUncontrolledValue(e.target.value)
      onChange?.(e)
    }

    const handleKeyDown: React.KeyboardEventHandler<HTMLTextAreaElement> = (
      e
    ) => {
      // antd fires onPressEnter on a bare Enter only — Shift+Enter is a
      // newline, and a textarea that submitted on it would be unusable.
      if (e.key === 'Enter' && !e.shiftKey) onPressEnter?.(e)
      onKeyDown?.(e)
    }

    const minRows = typeof autoSize === 'object' ? autoSize.minRows : undefined
    const maxRows = typeof autoSize === 'object' ? autoSize.maxRows : undefined

    const count = String(value ?? '').length
    let countLabel: React.ReactNode = count
    if (typeof showCount === 'object' && showCount.formatter) {
      countLabel = showCount.formatter({
        value: String(value ?? ''),
        count,
        maxLength: maxLength as number | undefined,
      })
    } else if (maxLength) {
      countLabel = `${count}/${maxLength}`
    }

    return (
      <div
        data-slot="textarea-wrapper"
        className={cn('relative', className)}
        style={style}
      >
        <TextareaPrimitive
          aria-invalid={status === 'error' || undefined}
          {...(rest as React.ComponentProps<'textarea'>)}
          ref={textareaRef}
          value={value as string | number | readonly string[]}
          disabled={disabled}
          maxLength={maxLength}
          rows={autoSize ? undefined : rows}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          style={{
            resize: autoSize ? 'none' : undefined,
            minHeight: minRows ? `${minRows * 1.5}em` : undefined,
            maxHeight: maxRows ? `${maxRows * 1.5}em` : undefined,
          }}
          className={cn(
            size === 'small' && 'text-sm',
            statusClassName(status),
            variantClassName(
              variant ?? (bordered === false ? 'borderless' : 'outlined')
            )
          )}
        />
        {showCount ? (
          <div
            data-slot="textarea-count"
            className="pointer-events-none absolute bottom-1 right-2 text-xs text-muted-foreground"
          >
            {countLabel}
          </div>
        ) : null}
      </div>
    )
  }
)
TextArea.displayName = 'Input.TextArea'

const Password = React.forwardRef<InputRef, PasswordProps>(
  ({ visibilityToggle = true, iconRender, suffix, ...rest }, ref) => {
    const toggleConfig =
      typeof visibilityToggle === 'object' ? visibilityToggle : {}
    const [uncontrolledVisible, setUncontrolledVisible] = React.useState(false)
    const visible = toggleConfig.visible ?? uncontrolledVisible
    const canToggle = Boolean(visibilityToggle)

    const setVisible = (next: boolean) => {
      if (toggleConfig.visible === undefined) setUncontrolledVisible(next)
      toggleConfig.onVisibleChange?.(next)
    }

    const defaultIcon = visible ? <EyeOutlined /> : <EyeInvisibleOutlined />
    const toggleIcon = iconRender ? iconRender(visible) : defaultIcon

    return (
      <InputBase
        {...rest}
        ref={ref}
        type={visible ? 'text' : 'password'}
        suffix={
          canToggle ? (
            /* Flush for the same reason as `input-clear-icon` above: an
               icon-only affix inside the wrapper that owns the padding. */
            <button
              type="button"
              data-slot="input-password-toggle"
              aria-label={visible ? 'hide password' : 'show password'}
              className="flex cursor-pointer items-center text-muted-foreground/60 hover:text-muted-foreground"
              onClick={() => setVisible(!visible)}
            >
              {toggleIcon}
            </button>
          ) : (
            suffix
          )
        }
      />
    )
  }
)
Password.displayName = 'Input.Password'

const Search = React.forwardRef<InputRef, SearchProps>(
  ({ onSearch, enterButton, loading, suffix, onPressEnter, ...rest }, ref) => {
    const innerRef = React.useRef<InputRef | null>(null)

    const setRef = (node: InputRef | null) => {
      innerRef.current = node
      if (typeof ref === 'function') ref(node as InputRef)
      // eslint-disable-next-line no-param-reassign
      else if (ref) ref.current = node as InputRef
    }

    const fireSearch = (
      event:
        | React.MouseEvent<HTMLElement>
        | React.KeyboardEvent<HTMLInputElement>
    ) => {
      const value = innerRef.current?.input?.value ?? String(rest.value ?? '')
      onSearch?.(value, event, { source: 'input' })
    }

    return (
      <InputBase
        {...rest}
        ref={setRef}
        onPressEnter={(e) => {
          fireSearch(e)
          onPressEnter?.(e)
        }}
        suffix={
          suffix ?? (
            /* Flush for the same reason as `input-clear-icon` above: an
               icon-only affix inside the wrapper that owns the padding. */
            <button
              type="button"
              data-slot="input-search-button"
              aria-label="search"
              onClick={fireSearch}
              className="flex cursor-pointer items-center text-muted-foreground hover:text-foreground"
            >
              {loading ? (
                <LoadingOutlined />
              ) : (
                enterButton ?? <SearchOutlined />
              )}
            </button>
          )
        }
      />
    )
  }
)
Search.displayName = 'Input.Search'

export const Input = Object.assign(InputBase, {
  TextArea,
  Password,
  Search,
})
